import type {
  AccountBilling,
  BillingPendingChange,
  BillingPortalLink,
  BillingSubscription,
  CancelBillingResult,
  ReactivateBillingResult,
  SeatQuantityChangeResult,
} from "@/domain/model/AccountBilling";
import type {
  BillingPendingChangeResponseDto,
  BillingSubscriptionResponseDto,
  CancelBillingResponseDto,
  DecreaseSeatsResponseDto,
  GetAccountBillingResponseDto,
  GetBillingPortalResponseDto,
  IncreaseSeatsResponseDto,
  ReactivateBillingResponseDto,
} from "../dto/AccountBillingDto";

function toDate(value: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function mapSubscriptionDto(
  dto: BillingSubscriptionResponseDto,
): BillingSubscription {
  return {
    status: dto.status,
    purchasedSeatQuantity: dto.purchasedSeatQuantity,
    usedSeatQuantity: dto.usedSeatQuantity,
    freeSeatQuantity: dto.freeSeatQuantity,
    cancelAtPeriodEnd: dto.cancelAtPeriodEnd,
    currentPeriodStart: toDate(dto.currentPeriodStart),
    currentPeriodEnd: toDate(dto.currentPeriodEnd),
    currency: dto.currency,
    pricePerSeatNetCents: dto.pricePerSeatNetCents,
    vatRateBps: dto.vatRateBps,
  };
}

function mapPendingChangeDto(
  dto: BillingPendingChangeResponseDto,
): BillingPendingChange {
  return {
    id: dto.id,
    type: dto.type,
    status: dto.status,
    requestedSeatQuantity: dto.requestedSeatQuantity,
    activeSeatQuantity: dto.activeSeatQuantity,
    scheduledSeatQuantity: dto.scheduledSeatQuantity,
    takesEffectAt: toDate(dto.takesEffectAt),
    createdAt: toDate(dto.createdAt),
    updatedAt: toDate(dto.updatedAt),
  };
}

export function mapGetAccountBillingResponseDtoToModel(
  dto: GetAccountBillingResponseDto,
): AccountBilling {
  return {
    subscription: mapSubscriptionDto(dto.subscription),
    pendingChange: dto.pendingChange
      ? mapPendingChangeDto(dto.pendingChange)
      : null,
  };
}

export function mapIncreaseSeatsResponseDtoToModel(
  dto: IncreaseSeatsResponseDto,
): SeatQuantityChangeResult {
  return {
    changeRequestId: dto.changeRequestId,
    status: dto.status,
    requestedSeatQuantity: dto.requestedSeatQuantity,
    currentSeatQuantity: dto.currentSeatQuantity,
  };
}

export function mapDecreaseSeatsResponseDtoToModel(
  dto: DecreaseSeatsResponseDto,
): SeatQuantityChangeResult {
  return {
    changeRequestId: dto.changeRequestId,
    status: dto.status,
    requestedSeatQuantity: dto.requestedSeatQuantity,
    currentSeatQuantity: dto.currentSeatQuantity,
  };
}

export function mapCancelBillingResponseDtoToModel(
  dto: CancelBillingResponseDto,
): CancelBillingResult {
  return {
    changeRequestId: dto.changeRequestId,
    status: dto.status,
    cancelAtPeriodEnd: dto.cancelAtPeriodEnd,
  };
}

export function mapReactivateBillingResponseDtoToModel(
  dto: ReactivateBillingResponseDto,
): ReactivateBillingResult {
  return {
    changeRequestId: dto.changeRequestId,
    status: dto.status,
  };
}

export function mapBillingPortalResponseDtoToModel(
  dto: GetBillingPortalResponseDto,
): BillingPortalLink {
  return {
    url: dto.url,
  };
}
